import { useEffect, useState } from 'react';

import { api } from '../../api/client';
import type { Company, CertificateValidation, HealthCheck, PadronHealthCheck, SalePointRef } from '../../api/types';
import { PadronStatus } from './PadronStatus';
import {
  AuthorizeInvoicing,
  BeforeYouStart,
  CreateCertificate,
  CreateSalePoint,
  EnableCertificatesService,
  GuideFootnote,
  GuideStep,
  ivaLabel,
} from './ArcaGuide';

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// El core exige que el PEM termine exactamente en "-----END CERTIFICATE-----\n". Al copiar el .crt
// es habitual perder el salto final o arrastrar espacios, y eso fallaba como "Invalid format".
export function normalizePem(text: string): string {
  return `${text.trim()}\n`;
}

export function OnboardingPage({ defaultCompanyUrn }: { defaultCompanyUrn: string | null }) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyUrn, setCompanyUrn] = useState<string>(defaultCompanyUrn ?? '');
  const [company, setCompany] = useState<Company | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [csrAlias, setCsrAlias] = useState<string | null>(null);
  const [certPem, setCertPem] = useState('');
  const [validation, setValidation] = useState<CertificateValidation | null>(null);
  const [health, setHealth] = useState<HealthCheck | null>(null);
  const [salePoints, setSalePoints] = useState<SalePointRef[]>([]);
  const [salePointsLoaded, setSalePointsLoaded] = useState(false);
  const [padron, setPadron] = useState<PadronHealthCheck | null>(null);
  const [padronFailed, setPadronFailed] = useState(false);

  useEffect(() => {
    void loadPadron();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api
      .listCompanies()
      .then((list) => {
        setCompanies(list);
        if (!companyUrn && list[0]) setCompanyUrn(list[0].urn);
      })
      .catch((e) => setError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!companyUrn) return;
    setCsrAlias(null);
    setSalePoints([]);
    setSalePointsLoaded(false);
    setHealth(null);
    api
      .getCompany(companyUrn)
      .then(setCompany)
      .catch((e) => setError((e as Error).message));
  }, [companyUrn]);

  const reset = () => {
    setError(null);
    setOk(null);
  };

  async function downloadCsr() {
    reset();
    try {
      const { certificate, alias } = await api.getCsr(companyUrn);
      downloadText(`${alias || 'pedido'}.csr`, certificate);
      setCsrAlias(alias || null);
      setOk('Pedido de certificado descargado. Seguí con los pasos en ARCA.');
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function validate() {
    reset();
    setValidation(null);
    try {
      setValidation(await api.validateCertificate(normalizePem(certPem)));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function upload() {
    reset();
    try {
      const updated = await api.uploadArcaCertificate(companyUrn, normalizePem(certPem));
      setCompany(updated);
      setOk('Certificado de ARCA cargado correctamente.');
      setCertPem('');
      setValidation(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  // Informativo: una falla del padrón no se muestra como error del onboarding.
  async function loadPadron() {
    setPadronFailed(false);
    try {
      setPadron(await api.padronHealthCheck());
    } catch {
      setPadron(null);
      setPadronFailed(true);
    }
  }

  async function checkHealth() {
    reset();
    void loadPadron();
    try {
      setHealth(await api.healthCheck());
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function loadSalePoints() {
    reset();
    try {
      setSalePoints(await api.salePoints());
      setSalePointsLoaded(true);
    } catch (e) {
      setSalePointsLoaded(false);
      setError(
        `${(e as Error).message}. Revisá que el certificado esté cargado (paso 5) y autorizado para Facturación Electrónica (paso 4).`,
      );
    }
  }

  const arcaValidTo = company?.certificates?.arca?.validTo;
  const alias = csrAlias ?? company?.certificates?.base?.alias;

  return (
    <div>
      <div className="card">
        <h3>Empresa / Sucursal</h3>
        <div className="row">
          <select value={companyUrn} onChange={(e) => setCompanyUrn(e.target.value)}>
            <option value="">Seleccionar…</option>
            {companies.map((c) => (
              <option key={c.urn} value={c.urn}>
                {c.fantasyName || c.legalName} — CUIT {c.taxId}
              </option>
            ))}
          </select>
        </div>
        {company && (
          <div className="row" style={{ marginTop: 12 }}>
            <div>
              <div className="muted">Condición IVA</div>
              <div>{ivaLabel(company.ivaCondition)}</div>
            </div>
            <div>
              <div className="muted">Certificado ARCA</div>
              <div>{arcaValidTo ? `vence ${String(arcaValidTo).slice(0, 10)}` : 'no cargado'}</div>
            </div>
            <div>
              <div className="muted">Autenticación AFIP</div>
              <div>{company.authentication?.dates?.to ? 'activa' : 'pendiente'}</div>
            </div>
          </div>
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {ok && <div className="ok">{ok}</div>}

      {company && (
        <>
          <BeforeYouStart taxId={company.taxId} />

          <GuideStep number={1} title="Descargá el pedido de certificado (CSR)" where="En este sistema" done={!!csrAlias}>
            <p className="muted">
              El archivo <strong>.csr</strong> solo contiene datos públicos de la empresa. La clave privada queda guardada
              en este sistema y nunca se descarga.
            </p>
            <button className="btn" onClick={downloadCsr} disabled={!companyUrn}>
              Descargar CSR
            </button>
          </GuideStep>

          <GuideStep
            number={2}
            title="Habilitá la administración de certificados"
            where="En ARCA · solo si todavía no tenés el servicio"
          >
            <EnableCertificatesService />
          </GuideStep>

          <GuideStep number={3} title="Creá el certificado" where="En ARCA · Administración de Certificados Digitales">
            <CreateCertificate taxId={company.taxId} alias={alias} />
          </GuideStep>

          <GuideStep
            number={4}
            title="Autorizá el certificado para facturar"
            where="En ARCA · Administrador de Relaciones de Clave Fiscal"
          >
            <AuthorizeInvoicing taxId={company.taxId} />
          </GuideStep>

          <GuideStep number={5} title="Cargá el certificado" where="En este sistema" done={!!arcaValidTo}>
            <p className="muted">
              Abrí el archivo <strong>.crt</strong> con el Bloc de notas, copiá todo el contenido (desde{' '}
              <code>-----BEGIN CERTIFICATE-----</code> hasta <code>-----END CERTIFICATE-----</code>) y pegalo acá.
            </p>
            <textarea
              placeholder="Pegá acá el certificado .crt (formato PEM)"
              value={certPem}
              onChange={(e) => {
                setCertPem(e.target.value);
                setValidation(null);
              }}
            />
            {validation && (
              <div className={validation.valid ? 'ok' : 'error'} style={{ marginTop: 8 }}>
                {validation.valid
                  ? `Válido (vence ${String(validation.validTo).slice(0, 10)})`
                  : `Inválido: ${validation.reason ?? 'revisar'}`}
              </div>
            )}
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn secondary" onClick={validate} disabled={!certPem.trim()}>
                Validar
              </button>
              <button
                className="btn"
                onClick={upload}
                disabled={!certPem.trim() || (validation ? !validation.valid : false)}
              >
                Guardar certificado
              </button>
            </div>
          </GuideStep>

          <GuideStep
            number={6}
            title="Creá un punto de venta para facturar desde el sistema"
            where="En ARCA · Administración de puntos de venta y domicilios"
          >
            <CreateSalePoint taxId={company.taxId} ivaCondition={company.ivaCondition} />
          </GuideStep>

          <GuideStep
            number={7}
            title="Verificá la conexión"
            where="En este sistema"
            done={salePointsLoaded && salePoints.length > 0}
          >
            <p className="muted">
              <strong>Cargar puntos de venta</strong> consulta ARCA con el certificado cargado: si aparece el punto de
              venta del paso 6, el certificado y la autorización funcionan.
            </p>
            <div className="row">
              <button className="btn" onClick={loadSalePoints}>
                Cargar puntos de venta
              </button>
              <button className="btn secondary" onClick={checkHealth}>
                Probar conexión AFIP
              </button>
            </div>
            {health && (
              <div className="row" style={{ marginTop: 12 }}>
                <span className="muted">App: {health.AppServer ?? '-'}</span>
                <span className="muted">DB: {health.DbServer ?? '-'}</span>
                <span className="muted">Auth: {health.AuthServer ?? '-'}</span>
              </div>
            )}
            <PadronStatus check={padron} failed={padronFailed} />
            {salePointsLoaded && !salePoints.length && (
              <p className="note">
                ARCA no devolvió puntos de venta para esta CUIT. Revisá que el del paso 6 sea de tipo Webservices.
              </p>
            )}
            {!!salePoints.length && (
              <table style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Nro</th>
                    <th>Tipo emisión</th>
                    <th>Bloqueado</th>
                  </tr>
                </thead>
                <tbody>
                  {salePoints.map((sp) => (
                    <tr key={sp.nro}>
                      <td>{sp.nro}</td>
                      <td>{sp.emisionTipo ?? '-'}</td>
                      <td>{sp.bloqueado ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </GuideStep>

          <GuideFootnote />
        </>
      )}
    </div>
  );
}
